import React, { useState, useEffect } from 'react';
import KanbanView from './KanbanView';
import { Users, CheckCircle, X, Clock } from 'lucide-react';
import CandidateProfile from './CandidateProfile';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

const avatar = (name, size=36) => {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];
  const c = colors[name?.charCodeAt(0)%colors.length];
  return <div style={{width:`${size}px`,height:`${size}px`,borderRadius:'50%',background:c,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:`${size/2.5}px`,flexShrink:0}}>{name?.charAt(0).toUpperCase()}</div>;
};

export default function CandidatesPage({ token, requirements, user }) {
  const [selectedReq, setSelectedReq] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scheduleCandidate, setScheduleCandidate] = useState(null);
  const [schedDate, setSchedDate] = useState(null);
  const [schedSlots, setSchedSlots] = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);

  const loadCandidates = async (reqId) => {
    if (!reqId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/candidates/${reqId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setCandidates(d.candidates || []);
    } catch {}
    setLoading(false);
  };

  const sendFeedbackRequest = async (id) => {
    try {
      const r = await fetch(`${API}/feedback/send/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) setMsg('✅ Feedback request sent to client!');
      else setMsg(d.message);
    } catch { setMsg('Error!'); }
  };

  const approveCandidate = async (id, status) => {
    try {
      const r = await fetch(`${API}/candidates/approve/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const d = await r.json();
      if (d.success) {
        setMsg(status === 'approved' ? '✅ Approved! Interview link sent to vendor.' : '❌ Candidate rejected.');
        loadCandidates(selectedReq);
      }
    } catch {}
  };

  const shortlist = async (id, status) => {
    try {
      const r = await fetch(`${API}/candidates/shortlist/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const d = await r.json();
      if (d.success) { setMsg(`Candidate ${status}!`); loadCandidates(selectedReq); }
    } catch {}
  };

  const submitSchedule = async () => {
    if (!schedDate || schedSlots.length === 0) return;
    setSchedLoading(true);
    try {
      const r = await fetch(`${API}/candidates/${scheduleCandidate._id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: schedDate.toISOString().split('T')[0], slots: schedSlots })
      });
      const d = await r.json();
      if (d.success) {
        setMsg('✅ Interview slots sent to vendor!');
        setScheduleCandidate(null);
        setSchedDate(null);
        setSchedSlots([]);
        loadCandidates(selectedReq);
      }
    } catch {}
    setSchedLoading(false);
  };

  const toggleSchedSlot = (slot) => {
    if (schedSlots.includes(slot)) setSchedSlots(schedSlots.filter(s => s !== slot));
    else if (schedSlots.length < 3) setSchedSlots([...schedSlots, slot]);
  };

  const scoreColor = (score) => score >= 8 ? '#16a34a' : score >= 6 ? '#ca8a04' : '#dc2626';
  const scoreBg = (score) => score >= 8 ? '#dcfce7' : score >= 6 ? '#fef9c3' : '#fee2e2';

  const dates = Array.from({length:10},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()+i); return d;
  }).filter(d=>d.getDay()!==0&&d.getDay()!==6);

  const TIME_SLOTS = ['9:00-10:00 AM','10:00-11:00 AM','11:00-12:00 PM','12:00-1:00 PM','1:00-2:00 PM','2:00-3:00 PM','3:00-4:00 PM','4:00-5:00 PM','5:00-6:00 PM'];

  return (
    <div>
      <KanbanView token={token} requirements={requirements} user={user}/>
      <div style={{display:'none'}}><span>
        {msg && <div style={{padding:'12px 16px',background:'#dcfce7',color:'#16a34a',borderRadius:'8px',marginBottom:'16px',fontSize:'14px'}}>{msg}</div>}

      {/* Requirement selector */}
      <div style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #e2e8f0',marginBottom:'20px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
        <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT REQUIREMENT</label>
        <select value={selectedReq} onChange={e=>{setSelectedReq(e.target.value);loadCandidates(e.target.value);}}
          style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',color:'#1e293b',fontSize:'14px',outline:'none',background:'#f8fafc',fontFamily:'inherit'}}>
          <option value="">-- Select a requirement --</option>
          {requirements.map(r=><option key={r._id} value={r._id}>{r.title} ({r.status})</option>)}
        </select>
      </div>

      {loading && <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading candidates...</div>}

      {!loading && selectedReq && candidates.length > 0 && (
        <div style={{display:'grid',gap:'16px'}}>
          {candidates.map(c=>(
            <div key={c._id} style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #e2e8f0',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
              <div style={{display:'flex',gap:'14px',alignItems:'flex-start',marginBottom:'14px'}}>
                {avatar(c.name)}
                <div style={{flex:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <h4 style={{color:'#1e293b',fontWeight:'700',fontSize:'15px',margin:'0 0 4px'}}>{c.name}</h4>
                      <span style={{color:'#64748b',fontSize:'13px'}}>{c.experience} yrs • Rs.{c.expectedRate?.toLocaleString()}/mo • {c.availability}</span>
                    </div>
                    <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                      {c.aiScore&&<span style={{padding:'4px 10px',borderRadius:'20px',fontSize:'12px',fontWeight:'700',background:scoreBg(c.aiScore),color:scoreColor(c.aiScore)}}>AI: {c.aiScore}/10</span>}
                      <span style={{padding:'4px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',
                        background:c.status==='shortlisted'?'#dcfce7':c.status==='rejected'?'#fee2e2':'#fef9c3',
                        color:c.status==='shortlisted'?'#16a34a':c.status==='rejected'?'#dc2626':'#ca8a04'}}>
                        {c.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {(c.skills||[]).length>0&&(
                <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'12px'}}>
                  {c.skills.map(s=><span key={s} style={{padding:'2px 8px',background:'#ede9fe',color:'#6366f1',borderRadius:'4px',fontSize:'11px'}}>{s}</span>)}
                </div>
              )}

              {/* Action buttons */}
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                <button onClick={()=>setSelectedCandidate(c)} style={{padding:'6px 14px',background:'#f1f5f9',color:'#1e293b',border:'1px solid #e2e8f0',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600',fontFamily:'inherit'}}>View More</button>
                
                {/* AI Report PDF */}
                {c.aiScore && (
                  <button onClick={async()=>{
                    try {
                      const r = await fetch(`${API}/candidates/report/${c._id}`,{headers:{Authorization:`Bearer ${token}`}});
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `AI_Report_${c.name.replace(/\s/g,'_')}.pdf`;
                      a.click();
                    } catch(e) { alert('Report generation failed!'); }
                  }} style={{padding:'6px 14px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600',fontFamily:'inherit'}}>
                    📄 AI Report
                  </button>
                )}

                {/* Resume - show for all candidates */}
                <button onClick={async()=>{
                  try {
                    const r = await fetch(`${API}/candidates/resume/${c._id}`,{headers:{Authorization:`Bearer ${token}`}});
                    if(!r.ok){ alert('Resume not available'); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Resume_${c.name.replace(/\s/g,'_')}.pdf`;
                    a.click();
                  } catch(e) { alert('Resume not available!'); }
                }} style={{padding:'6px 14px',background:'#f0fdf4',color:'#16a34a',border:'1px solid #86efac',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600',fontFamily:'inherit'}}>
                  📎 Resume
                </button>

                {c.status==='shortlisted'&&(
                  <>
                    <button onClick={()=>setScheduleCandidate(c)} style={{padding:'5px 12px',background:'white',color:'#16a34a',border:'1px solid #86efac',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>📅 Schedule</button>
                    <button onClick={()=>sendFeedbackRequest(c._id)} style={{padding:'5px 12px',background:'white',color:'#6366f1',border:'1px solid #c4b5fd',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>📝 Request Feedback</button>
                  </>
                )}

                {c.scheduleStatus==='confirmed'&&c.meetingLink&&(
                  <a href={c.meetingLink} target="_blank" rel="noreferrer" style={{padding:'6px 14px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',borderRadius:'6px',textDecoration:'none',fontSize:'12px',fontWeight:'600'}}>🎯 Join Meeting</a>
                )}

                {/* Admin approve/reject */}
                {c.status==='pending'&&(
                  <>
                    <button onClick={()=>approveCandidate(c._id,'approved')} style={{padding:'5px 12px',background:'white',color:'#16a34a',border:'1px solid #86efac',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>✅ Approve + Send Interview</button>
                    <button onClick={()=>approveCandidate(c._id,'rejected')} style={{padding:'5px 12px',background:'white',color:'#dc2626',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>❌ Reject</button>
                  </>
                )}
                {c.status!=='pending'&&c.status!=='shortlisted'&&c.status!=='rejected'&&(
                  <>
                    <button onClick={()=>shortlist(c._id,'shortlisted')} style={{padding:'5px 12px',background:'white',color:'#16a34a',border:'1px solid #86efac',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>✅ Shortlist</button>
                    <button onClick={()=>shortlist(c._id,'rejected')} style={{padding:'5px 12px',background:'white',color:'#dc2626',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>❌ Reject</button>
                  </>
                )}
                {c.interviewCompleted&&c.aiScore&&(
                  <span style={{padding:'4px 10px',background:'#f0fdf4',color:'#16a34a',borderRadius:'6px',fontSize:'12px',fontWeight:'600',border:'1px solid #86efac'}}>🤖 AI Score: {c.aiScore}/10</span>
                )}
                {c.status==='interviewed'&&!c.interviewCompleted&&(
                  <span style={{padding:'4px 10px',background:'#fffbeb',color:'#ca8a04',borderRadius:'6px',fontSize:'12px',fontWeight:'600',border:'1px solid #fde68a'}}>⏳ Interview Pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && selectedReq && candidates.length===0 && (
        <div style={{textAlign:'center',padding:'60px',background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',color:'#94a3b8'}}>
          <Users size={48} color="#e2e8f0" style={{margin:'0 auto 16px',display:'block'}}/>
          <p style={{margin:0,fontSize:'15px'}}>No candidates submitted yet</p>
        </div>
      )}

      {selectedCandidate && <CandidateProfile candidate={selectedCandidate} token={token} userRole="client" onClose={()=>setSelectedCandidate(null)}/>}

      {scheduleCandidate && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'16px',padding:'28px',maxWidth:'520px',width:'100%',boxShadow:'0 40px 80px rgba(0,0,0,0.2)',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{color:'#1e293b',fontSize:'16px',fontWeight:'700',margin:0}}>📅 Schedule Interview — {scheduleCandidate.name}</h3>
              <button onClick={()=>setScheduleCandidate(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:'18px'}}>✕</button>
            </div>
            <p style={{color:'#64748b',fontSize:'13px',margin:'0 0 16px'}}>Select a date and up to 3 time slots. Vendor will confirm one slot.</p>
            <div style={{marginBottom:'16px'}}>
              <label style={{color:'#64748b',fontSize:'11px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT DATE</label>
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                {dates.map(d=>(
                  <div key={d.toISOString()} onClick={()=>setSchedDate(d)}
                    style={{padding:'7px 12px',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                      background:schedDate?.toDateString()===d.toDateString()?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                      color:schedDate?.toDateString()===d.toDateString()?'white':'#1e293b',
                      border:schedDate?.toDateString()===d.toDateString()?'none':'1px solid #e2e8f0'}}>
                    {d.toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}
                  </div>
                ))}
              </div>
            </div>
            {schedDate && (
              <div style={{marginBottom:'20px'}}>
                <label style={{color:'#64748b',fontSize:'11px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT UP TO 3 SLOTS ({schedSlots.length}/3)</label>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                  {TIME_SLOTS.map(slot=>(
                    <div key={slot} onClick={()=>toggleSchedSlot(slot)}
                      style={{padding:'10px',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'600',textAlign:'center',
                        background:schedSlots.includes(slot)?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                        color:schedSlots.includes(slot)?'white':'#1e293b',
                        border:schedSlots.includes(slot)?'none':'1px solid #e2e8f0',
                        opacity:!schedSlots.includes(slot)&&schedSlots.length>=3?0.4:1}}>
                      {slot}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>setScheduleCandidate(null)} style={{flex:1,padding:'11px',background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600'}}>Cancel</button>
              <button onClick={submitSchedule} disabled={!schedDate||schedSlots.length===0||schedLoading}
                style={{flex:2,padding:'11px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600',opacity:(!schedDate||schedSlots.length===0)?0.5:1}}>
                {schedLoading?'Sending...':'Send Slots to Vendor →'}
              </button>
            </div>
          </div>
        </div>
      )}
      </span></div>
    </div>
  );
}
